import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/integrations/supabase/client";
import { appQueryKeys } from "@/lib/queryKeys";

type QueryResult<T> = Promise<{ data: T[] | null; error: unknown }>;

interface PracticeFocusReviewQueryBuilder<T> {
  select(columns: string): {
    eq(column: string, value: string): PracticeFocusReviewFilterBuilder<T>;
  };
}

interface PracticeFocusReviewFilterBuilder<T> {
  eq(column: string, value: string): PracticeFocusReviewFilterBuilder<T>;
  in(column: string, values: string[]): PracticeFocusReviewFilterBuilder<T>;
  order(column: string, options?: { ascending?: boolean; nullsFirst?: boolean }): {
    limit(count: number): QueryResult<T>;
  };
}

interface PracticeFocusReviewDbClient {
  from(table: "practice_focus_suggestions"): PracticeFocusReviewQueryBuilder<CoachPracticeFocusSuggestionRow>;
  from(table: "coach_practice_focus_suggestion_evidence"): PracticeFocusReviewQueryBuilder<CoachPracticeFocusEvidenceRow>;
}

interface PracticeFocusApprovalDbClient {
  rpc(
    fn: "approve_practice_focus_suggestion_for_leader",
    args: {
      p_suggestion_id: string;
      p_leader_id: string;
      p_evidence_summaries: Array<{ evidenceId: string; leaderSafeSummary: string }>;
    },
  ): Promise<{
    data: Array<{
      suggestion_id: string;
      approved_evidence_rows: number;
      approved_at: string;
    }> | null;
    error: unknown;
  }>;
}

interface CoachPracticeFocusSuggestionRow {
  id: string;
  leader_id: string;
  coach_id: string | null;
  focus_key: string;
  title: string;
  practice_prompt: string;
  rationale_summary: string | null;
  confidence_level: string | null;
  priority_score: number | null;
  primary_evidence_count: number | null;
  last_observed_at: string | null;
  created_at: string | null;
}

interface CoachPracticeFocusEvidenceRow {
  id: string;
  suggestion_id: string;
  leader_id: string;
  source_type: string;
  source_id: string;
  source_key: string | null;
  graph_node_id: string | null;
  evidence_role: string;
  evidence_visibility_scope: string;
  leader_safe_summary: string | null;
  observed_at: string | null;
  created_at: string | null;
}

export interface CoachPracticeFocusReviewEvidence {
  id: string;
  suggestionId: string;
  role: string;
  sourceType: string;
  leaderSafeSummary: string | null;
  observedAt: string | null;
}

export interface CoachPracticeFocusReviewItem {
  id: string;
  leaderId: string;
  coachId: string | null;
  focusKey: string;
  title: string;
  practicePrompt: string;
  rationaleSummary: string | null;
  confidenceLevel: string | null;
  priorityScore: number | null;
  primaryEvidenceCount: number;
  observedAt: string | null;
  evidence: CoachPracticeFocusReviewEvidence[];
}

export interface PracticeFocusEvidenceApprovalSummary {
  evidenceId: string;
  leaderSafeSummary: string;
}

export interface ApproveCoachPracticeFocusSuggestionInput {
  suggestionId: string;
  leaderId: string;
  coachId: string;
  evidenceSummaries: PracticeFocusEvidenceApprovalSummary[];
  dbClient?: PracticeFocusApprovalDbClient;
}

function isMissingSchemaObject(error: unknown) {
  const err = error as { code?: string; message?: string; details?: string; hint?: string } | null;
  if (!err) return false;
  if (err.code === "42P01" || err.code === "42703" || err.code === "PGRST204" || err.code === "PGRST205") {
    return true;
  }
  const message = `${err.message || ""} ${err.details || ""} ${err.hint || ""}`.toLowerCase();
  return (
    message.includes("does not exist")
    || message.includes("could not find the table")
    || message.includes("could not find the column")
    || message.includes("schema cache")
  );
}

function evidenceRowToReviewEvidence(row: CoachPracticeFocusEvidenceRow): CoachPracticeFocusReviewEvidence {
  const existingSummary = row.leader_safe_summary?.replace(/\s+/g, " ").trim();
  return {
    id: row.id,
    suggestionId: row.suggestion_id,
    role: row.evidence_role,
    sourceType: row.source_type,
    leaderSafeSummary: existingSummary || null,
    observedAt: row.observed_at ?? row.created_at,
  };
}

export async function fetchCoachPracticeFocusReviewItems(
  params: {
    coachId: string | null | undefined;
    leaderId: string | null | undefined;
    dbClient?: PracticeFocusReviewDbClient;
  },
): Promise<CoachPracticeFocusReviewItem[]> {
  const { coachId, leaderId } = params;
  if (!coachId || !leaderId) return [];
  const dbClient = params.dbClient ?? (supabase as unknown as PracticeFocusReviewDbClient);

  const suggestionResult = await dbClient
    .from("practice_focus_suggestions")
    .select("id, leader_id, coach_id, focus_key, title, practice_prompt, rationale_summary, confidence_level, priority_score, primary_evidence_count, last_observed_at, created_at")
    .eq("leader_id", leaderId)
    .eq("coach_id", coachId)
    .eq("visibility_scope", "coach_reviewed")
    .eq("ledger_visibility_scope", "coach_only")
    .order("priority_score", { ascending: false, nullsFirst: false })
    .limit(5);

  if (suggestionResult.error) {
    if (isMissingSchemaObject(suggestionResult.error)) return [];
    throw suggestionResult.error;
  }

  const suggestionRows = suggestionResult.data ?? [];
  if (suggestionRows.length === 0) return [];

  const suggestionIds = suggestionRows.map((row) => row.id);
  const evidenceResult = await dbClient
    .from("coach_practice_focus_suggestion_evidence")
    .select("id, suggestion_id, leader_id, source_type, source_id, source_key, graph_node_id, evidence_role, evidence_visibility_scope, leader_safe_summary, observed_at, created_at")
    .eq("leader_id", leaderId)
    .in("suggestion_id", suggestionIds)
    .order("observed_at", { ascending: false, nullsFirst: false })
    .limit(50);

  if (evidenceResult.error) {
    if (isMissingSchemaObject(evidenceResult.error)) return [];
    throw evidenceResult.error;
  }

  const evidenceBySuggestion = new Map<string, CoachPracticeFocusReviewEvidence[]>();
  (evidenceResult.data ?? []).forEach((row) => {
    const next = evidenceRowToReviewEvidence(row);
    const bucket = evidenceBySuggestion.get(row.suggestion_id) ?? [];
    bucket.push(next);
    evidenceBySuggestion.set(row.suggestion_id, bucket);
  });

  return suggestionRows.map((row) => ({
    id: row.id,
    leaderId: row.leader_id,
    coachId: row.coach_id,
    focusKey: row.focus_key,
    title: row.title,
    practicePrompt: row.practice_prompt,
    rationaleSummary: row.rationale_summary,
    confidenceLevel: row.confidence_level,
    priorityScore: row.priority_score,
    primaryEvidenceCount: row.primary_evidence_count ?? 0,
    observedAt: row.last_observed_at ?? row.created_at,
    evidence: evidenceBySuggestion.get(row.id) ?? [],
  }));
}

export async function approveCoachPracticeFocusSuggestion(
  input: ApproveCoachPracticeFocusSuggestionInput,
): Promise<{ suggestionId: string; approvedEvidenceRows: number; approvedAt: string }> {
  const dbClient = input.dbClient ?? (supabase as unknown as PracticeFocusApprovalDbClient);
  const safeEvidenceSummaries = input.evidenceSummaries
    .map((summary) => ({
      evidenceId: summary.evidenceId,
      leaderSafeSummary: summary.leaderSafeSummary.replace(/\s+/g, " ").trim(),
    }))
    .filter((summary) => summary.evidenceId && summary.leaderSafeSummary);

  if (safeEvidenceSummaries.length === 0) {
    throw new Error("Practice Focus approval requires at least one leader-safe evidence summary.");
  }

  const result = await dbClient.rpc("approve_practice_focus_suggestion_for_leader", {
    p_suggestion_id: input.suggestionId,
    p_leader_id: input.leaderId,
    p_evidence_summaries: safeEvidenceSummaries,
  });

  if (result.error) throw result.error;
  const approved = result.data?.[0];
  if (!approved?.suggestion_id) {
    throw new Error("Practice Focus approval did not match a pending suggestion for this coach and leader.");
  }

  return {
    suggestionId: approved.suggestion_id,
    approvedEvidenceRows: approved.approved_evidence_rows,
    approvedAt: approved.approved_at,
  };
}

export function useCoachPracticeFocusReview(params: {
  coachId: string | null | undefined;
  leaderId: string | null | undefined;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["coach-practice-focus-review", params.coachId, params.leaderId],
    queryFn: () => fetchCoachPracticeFocusReviewItems(params),
    enabled: Boolean(params.enabled ?? true) && Boolean(params.coachId && params.leaderId),
  });
}

export function useApproveCoachPracticeFocusSuggestion(params: {
  coachId: string | null | undefined;
  leaderId: string | null | undefined;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: Omit<ApproveCoachPracticeFocusSuggestionInput, "coachId" | "leaderId">) => {
      if (!params.coachId || !params.leaderId) {
        throw new Error("Missing coach or leader id for Practice Focus approval.");
      }
      return approveCoachPracticeFocusSuggestion({
        ...input,
        coachId: params.coachId,
        leaderId: params.leaderId,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["coach-practice-focus-review", params.coachId, params.leaderId] });
      void queryClient.invalidateQueries({ queryKey: appQueryKeys.practiceFocusCard(params.leaderId ?? undefined) });
      void queryClient.invalidateQueries({ queryKey: ["coach-leaders-command"] });
    },
  });
}
